import { useEffect } from 'react';
import { useMap } from '@vis.gl/react-google-maps';
import isEmpty from 'lodash-es/isEmpty';

import useTheme from '@rover/kibble/themes/useTheme';
import { Geometry, ValidGeometryTypes } from '@rover/react-lib/src/components/GeoMap/types';

export interface GeoMapPolygonProps {
  geometry: Geometry;
  strokeWeight?: number;
  strokeColor?: string;
  fillColor?: string;
  fillOpacity?: number;
  clickable?: boolean;
}

const FEATURE_STYLE_PROPERTY = 'roverFeatureStyle';

/**
 * GeoMapPolygon component renders GeoJSON geometry on the map using map.data layer.
 * Supports all GeoJSON geometry types including Polygon, MultiPolygon, LineString, etc.
 */
const GeoMapPolygon = ({
  geometry,
  strokeWeight = 0,
  strokeColor,
  fillColor,
  fillOpacity = 0.5,
  clickable = true,
}: GeoMapPolygonProps): null => {
  const map = useMap();
  const { colors } = useTheme();
  const resolvedFillColor = fillColor || colors.blue['700'];

  useEffect(() => {
    if (!map) return undefined;
    if (isEmpty(geometry) || !geometry?.type || !ValidGeometryTypes.has(geometry.type as string)) {
      return undefined;
    }

    const featureStyle: google.maps.Data.StyleOptions = {
      fillColor: resolvedFillColor,
      fillOpacity,
      strokeWeight,
      strokeColor,
      clickable,
    };

    const geoJson = {
      type: 'FeatureCollection',
      features: [
        { type: 'Feature', geometry, properties: { [FEATURE_STYLE_PROPERTY]: featureStyle } },
      ],
    };

    const feature = map.data.addGeoJson(geoJson)[0];
    map.data.setStyle(
      (styledFeature: google.maps.Data.Feature) =>
        (styledFeature.getProperty(FEATURE_STYLE_PROPERTY) as
          | google.maps.Data.StyleOptions
          | undefined) ?? featureStyle
    );

    return () => {
      map.data.remove(feature);
    };
  }, [map, geometry, resolvedFillColor, fillOpacity, strokeWeight, strokeColor, clickable]);

  useEffect(() => {
    if (!map) return undefined;

    const fitPolygonBounds = (): void => {
      const bounds = new google.maps.LatLngBounds();
      let hasPoints = false;
      map.data.forEach((feature: google.maps.Data.Feature) => {
        feature.getGeometry()!.forEachLatLng((latLng: google.maps.LatLng) => {
          bounds.extend(latLng);
          hasPoints = true;
        });
      });
      if (hasPoints) {
        map.fitBounds(bounds);
      }
    };

    fitPolygonBounds();

    const mapDiv = map.getDiv();
    if (typeof ResizeObserver === 'undefined' || !mapDiv) return undefined;

    const observer = new ResizeObserver(() => fitPolygonBounds());
    observer.observe(mapDiv);
    return () => observer.disconnect();
  }, [map, geometry]);

  return null;
};

export default GeoMapPolygon;
